import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, endOfDay, differenceInDays } from "date-fns";

export interface HourlyData {
  hour: number;
  count: number;
}

export interface AttendanceStats {
  totalCount: number;
  byHour: HourlyData[];
  isMultipleDays: boolean;
  daysCount: number;
}

interface UseAttendanceStatsOptions {
  gymId: string;
  dateFrom?: Date;
  dateTo?: Date;
  enabled?: boolean;
}

export const useAttendanceStats = ({
  gymId,
  dateFrom,
  dateTo,
  enabled = true,
}: UseAttendanceStatsOptions) => {
  // Default to today if no dates provided
  const today = new Date();
  const effectiveFrom = dateFrom || today;
  const effectiveTo = dateTo || today;
  
  const rangeStart = startOfDay(effectiveFrom);
  const rangeEnd = endOfDay(effectiveTo);
  const daysCount = differenceInDays(rangeEnd, rangeStart) + 1;
  const isMultipleDays = daysCount > 1;

  return useQuery({
    queryKey: ["attendance-stats", gymId, rangeStart.toISOString(), rangeEnd.toISOString()],
    queryFn: async (): Promise<AttendanceStats> => {
      const { data, error } = await supabase
        .from("gym_attendance_logs")
        .select("check_in_at")
        .eq("gym_id", gymId)
        .gte("check_in_at", rangeStart.toISOString())
        .lte("check_in_at", rangeEnd.toISOString());

      if (error) throw error;

      // Initialize hours from 8am to 11pm (8-23)
      const hourlyMap = new Map<number, number>();
      for (let h = 8; h <= 23; h++) {
        hourlyMap.set(h, 0);
      }

      // Count entries per hour
      (data || []).forEach(record => {
        const hour = new Date(record.check_in_at).getHours();
        if (hour >= 8 && hour <= 23) {
          hourlyMap.set(hour, (hourlyMap.get(hour) || 0) + 1);
        }
      });

      const byHour: HourlyData[] = [];
      for (let h = 8; h <= 23; h++) {
        // If multiple days, show average per hour
        const rawCount = hourlyMap.get(h) || 0;
        const count = isMultipleDays ? Math.round(rawCount / daysCount) : rawCount;
        byHour.push({ hour: h, count });
      }

      return {
        totalCount: data?.length || 0,
        byHour,
        isMultipleDays,
        daysCount,
      };
    },
    enabled: enabled && !!gymId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};
